import React, { createContext, useContext, useCallback, useState, useEffect, useRef } from 'react';

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresIn: number;
}

interface CacheContextType {
  get: (key: string) => any | null;
  set: (key: string, data: any, expiresIn?: number) => void;
  remove: (key: string) => void;
  clear: () => void;
  has: (key: string) => boolean;
  isExpired: (key: string) => boolean;
  size: () => number;
  keys: () => string[];
}

const CacheContext = createContext<CacheContextType | undefined>(undefined);

const DEFAULT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const CacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cache] = useState(() => new Map<string, CacheEntry>());
  const [, forceUpdate] = useState({});

  // Clean up expired entries periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      let hasExpired = false;
      
      cache.forEach((entry, key) => {
        if (now - entry.timestamp > entry.expiresIn) {
          cache.delete(key);
          hasExpired = true;
        }
      });
      
      if (hasExpired) {
        forceUpdate({});
      }
    }, 60000); // Check every minute

    return () => clearInterval(cleanupInterval);
  }, [cache]);

  const get = useCallback((key: string) => {
    const entry = cache.get(key);
    
    if (!entry) {
      return null;
    }
    
    const now = Date.now();
    if (now - entry.timestamp > entry.expiresIn) {
      cache.delete(key);
      return null;
    }
    
    return entry.data;
  }, [cache]);

  const set = useCallback((key: string, data: any, expiresIn: number = DEFAULT_CACHE_DURATION) => {
    cache.set(key, {
      data,
      timestamp: Date.now(),
      expiresIn
    });
    forceUpdate({});
  }, [cache]);

  const remove = useCallback((key: string) => {
    const deleted = cache.delete(key);
    if (deleted) {
      forceUpdate({});
    }
  }, [cache]);

  const clear = useCallback(() => {
    cache.clear();
    forceUpdate({});
  }, [cache]);

  const has = useCallback((key: string) => {
    const entry = cache.get(key);
    if (!entry) return false;
    
    const now = Date.now();
    if (now - entry.timestamp > entry.expiresIn) {
      cache.delete(key);
      return false;
    }
    
    return true;
  }, [cache]);

  const isExpired = useCallback((key: string) => {
    const entry = cache.get(key);
    if (!entry) return true;
    
    const now = Date.now();
    return now - entry.timestamp > entry.expiresIn;
  }, [cache]);

  const size = useCallback(() => {
    return cache.size;
  }, [cache]);

  const keys = useCallback(() => {
    return Array.from(cache.keys());
  }, [cache]);

  const value: CacheContextType = {
    get,
    set,
    remove,
    clear,
    has,
    isExpired,
    size,
    keys
  };

  return (
    <CacheContext.Provider value={value}>
      {children}
    </CacheContext.Provider>
  );
};

export const useCache = () => {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within a CacheProvider');
  }
  return context;
};

// Higher-order hook for cached data fetching with proper cleanup
export function useCachedQuery<T>(
  key: string,
  fetchFn: () => Promise<T>,
  options?: {
    expiresIn?: number;
    dependencies?: any[];
    enabled?: boolean;
  }
) {
  const cache = useCache();
  const [data, setData] = useState<T | null>(() => cache.get(key));
  const [loading, setLoading] = useState(!data);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetch = useCallback(async () => {
    // Check cache first
    const cached = cache.get(key);
    if (cached) {
      if (isMountedRef.current) {
        setData(cached);
        setLoading(false);
      }
      return cached;
    }

    // Create new abort controller
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      if (isMountedRef.current) {
        setLoading(true);
        setError(null);
      }
      
      const result = await fetchFn();
      
      // Only update if component is still mounted
      if (isMountedRef.current) {
        // Update cache
        cache.set(key, result, options?.expiresIn);
        setData(result);
      }
      
      return result;
    } catch (err) {
      // Only update error if component is still mounted and not aborted
      if (isMountedRef.current && err instanceof Error && err.name !== 'AbortError') {
        const errorMessage = err.message;
        setError(errorMessage);
      }
      throw err;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [key, fetchFn, cache, options?.expiresIn]);

  useEffect(() => {
    isMountedRef.current = true;
    
    if (options?.enabled !== false) {
      fetch().catch(() => {
        // Error is already handled in fetch
      });
    }

    return () => {
      // Cleanup: mark as unmounted and abort any pending requests
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [fetch, options?.enabled, ...(options?.dependencies || [])]);

  const refetch = useCallback(async () => {
    cache.remove(key);
    return fetch();
  }, [cache, key, fetch]);

  const invalidate = useCallback(() => {
    cache.remove(key);
  }, [cache, key]);

  return { data, loading, error, refetch, invalidate };
}